import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';

export default function ApiKeyStatus({ providers, onKeySaved }) {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  // A managed key (the nyx-LLM-Gateway token) is env-only: the server refuses
  // to set or remove it here, so no field is offered for it.
  const providersWithKeys = providers.filter(p => p.requiresApiKey && !p.managedKey);

  const handleSave = async (providerId) => {
    const value = inputs[providerId]?.trim();
    if (!value) return;

    setSaving(providerId);
    setError(null);
    try {
      await apiClient.request(`/settings/providers/${providerId}/api-key`, {
        method: 'POST',
        body: JSON.stringify({ apiKey: value }),
      });
      setInputs(prev => ({ ...prev, [providerId]: '' }));
      await onKeySaved?.(providerId);
    } catch (err) {
      setError(`Failed to save key for ${providerId}: ${err.message}`);
    }
    setSaving(null);
  };

  const handleDelete = async (providerId) => {
    setError(null);
    try {
      await apiClient.request(`/settings/providers/${providerId}/api-key`, {
        method: 'DELETE',
      });
      await onKeySaved?.(providerId);
    } catch (err) {
      setError(`Failed to remove key for ${providerId}: ${err.message}`);
    }
  };

  return (
    <div className="api-key-manager">
      {error && <div className="api-key-error">{error}</div>}
      {providersWithKeys.map(provider => (
        <div key={provider.id} className="api-key-manager-item">
          <div className="api-key-manager-header">
            <span className={`api-key-icon ${provider.configured ? 'configured' : 'not-configured'}`}>
              {provider.configured ? '\u2713' : '\u25CB'}
            </span>
            <div className="api-key-manager-name">{provider.name}</div>
            <span className={`provider-status ${provider.configured ? 'configured' : 'not-configured'}`}>
              {provider.configured ? t('settings.configured', 'Configured') : t('settings.notConfigured', 'Not configured')}
            </span>
            {provider.configured && (
              <button
                className="api-key-delete-btn"
                onClick={() => handleDelete(provider.id)}
              >
                {t('settings.removeKey', 'Remove')}
              </button>
            )}
          </div>
          {!provider.configured && (
            <div className="api-key-manager-input-row">
              <input
                type="password"
                className="api-key-input"
                placeholder={`${provider.envKey}...`}
                value={inputs[provider.id] || ''}
                onChange={(e) => setInputs(prev => ({ ...prev, [provider.id]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(provider.id); }}
              />
              <button
                className="api-key-save-btn"
                disabled={!inputs[provider.id]?.trim() || saving === provider.id}
                onClick={() => handleSave(provider.id)}
              >
                {saving === provider.id ? '...' : t('settings.saveKey', 'Save')}
              </button>
            </div>
          )}
        </div>
      ))}

      <div className="api-key-manager-item">
        <div className="api-key-manager-header">
          <span className="api-key-icon configured">{'\u2713'}</span>
          <div className="api-key-manager-name">Ollama (Local)</div>
          <span className="provider-status configured">
            {t('settings.noKeyNeeded', 'No key needed')}
          </span>
        </div>
      </div>
    </div>
  );
}
